import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { formatDate } from 'utils';
import { CTP_ELGIBILITY_DETAILS, USER_TYPES } from 'utils/constants';
import { selectEntitlements } from 'services/session/sessionSelectors';

const selectUserState = (state: RootState) => state.user;

export const selectUserId = createSelector(
  [selectUserState],
  (userState) => userState.id,
);

export const selectUserName = createSelector(
  [selectUserState],
  (userState) => userState.name ?? {},
);

export const selectLoginUserName = createSelector(
  [selectUserState],
  (userState) => userState.username,
);

export const selectUserHasName = createSelector(
  [selectUserName],
  (userName) => !!userName.first,
);

export const selectUserDob = createSelector(
  [selectUserState],
  (userState) => userState.birthDate,
);

export const selectUserEmail = createSelector(
  [selectUserState],
  (userState) => userState.emailAddress,
);

export const selectUserLocale = createSelector(
  [selectUserState],
  (userState) => userState.locale,
);

export const selectUserLastVisitedAt = createSelector(
  [selectUserState],
  (userState) => userState.account?.lastVisitedAt,
);

export const selectUserSince = createSelector(
  [selectUserState],
  (userState) => userState.createdAt,
);

export const selectUserAddress = createSelector(
  [selectUserState],
  (userState) => userState.address ?? {},
);

export const selectUserPhoneNumber = createSelector(
  [selectUserState],
  (userState) => userState.phoneNumber,
);

export const selectServiceEndDate = createSelector(
  [selectUserState],
  (userState) => {
    if (userState.serviceEndDate) {
      return formatDate(userState.serviceEndDate, 'MM/DD/YYYY');
    }

    return '';
  },
);

export const selectServiceEndDateLong = createSelector(
  [selectUserState],
  (userState) => {
    if (userState.serviceEndDate) {
      const formatted = formatDate(userState.serviceEndDate, 'MMM D, YYYY');
      return formatted === 'NA' ? '' : formatted;
    }

    return '';
  },
);

export const selectUserLastFourSSN = createSelector(
  [selectUserState],
  (userState) => userState.ssnLastFour,
);

export const selectUserBillable = createSelector(
  [selectUserState],
  (userState) => userState.billable,
);

export const selectUserStatus = createSelector(
  [selectUserState],
  (userState) => userState.status,
);

/**
 * Account-holder type — `'primary'` or otherwise (`'secondary'`).
 *
 * Cancellation is a primary-holder-only capability (BRD FR-035 / BR-014), and
 * this is the single input for that gate. Angular branches the whole Billing
 * details body on it (`billing-term.component.html:3`), so it decides which body
 * renders, not merely whether one control is visible.
 */
export const selectUserType = createSelector(
  [selectUserState],
  (userState) => userState.type,
);

/**
 * True only when the account holder is **known** to be non-primary.
 *
 * Deliberately not `type !== 'primary'`. `initialState.type` is `''` and the user
 * slice is persist-whitelisted but empty on a first login, so a bare inequality
 * would resolve "not yet loaded" to "secondary" and flash the non-primary body —
 * heading with no plan name, paragraph with no payer — at a primary member
 * before their profile lands. It would also blank the billing body outright for
 * any account whose response omits `type`.
 *
 * Requiring a known, non-primary value keeps unknown on the full-body branch,
 * which is both today's behaviour and the safe default: the cost of a wrong
 * guess here is a member who cannot reach their own billing page.
 *
 * Angular has the same latent flaw — `(user$ | async)?.type === 'primary'`
 * resolves to the secondary template while the observable is null — so this is a
 * deliberate correction, not a ported behaviour (BRD FR-035 / BR-014).
 */
export const selectIsNonPrimaryUser = createSelector(
  [selectUserType],
  (type) => Boolean(type) && type !== USER_TYPES.primary,
);

/**
 * The primary account holder's display name, or `''` when unavailable
 * (BRD FR-050 / AC-048).
 *
 * Returns the empty string rather than `undefined` so the non-primary paragraph
 * can branch on truthiness and degrade to its name-less form instead of printing
 * "undefined" — which is what Angular does, since
 * `billing-term.component.html:216` interpolates `user.primaryUser.firstName`
 * with no guard at all.
 *
 * Note: the names come straight off `primaryUser`, with **no** `type` condition.
 * ADR note E3 reads `billing-details.component.ts:148` as gating the names on
 * `type === 'secondary'`, but those component fields feed a different template;
 * the paragraph itself reads `user.primaryUser` directly for every non-primary
 * type. Gating here would blank a name Angular shows.
 */
export const selectPrimaryUserName = createSelector(
  [selectUserState],
  (userState) => {
    const { primaryUser } = userState;
    if (!primaryUser) {
      return '';
    }
    return [primaryUser.firstName, primaryUser.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  },
);

/** Paid billing UI — billable can be set before status catches up to subscriber. */
export const selectShowConvertedToPaidBilling = createSelector(
  [selectUserBillable, selectUserStatus],
  (billable, status) =>
    Boolean(billable) || status === CTP_ELGIBILITY_DETAILS.subscriber,
);

export const selectUserVerification = createSelector(
  [selectUserState],
  (userState) => userState.verification,
);

export const selectUserHasTUID = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const verification = entitlements['itps:verification'];
    if (Array.isArray(verification)) return verification.includes('tuid');
    if (typeof verification === 'string') return verification === 'tuid';
    return false;
  },
);

export const selectUserHasXID = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const verification = entitlements['itps:verification'];
    if (Array.isArray(verification)) return verification.includes('xid');
    if (typeof verification === 'string') return verification === 'xid';
    return false;
  },
);

export const selectUserHasIDAuthOrEid = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const verification = entitlements['itps:verification'];
    if (Array.isArray(verification))
      return verification.includes('idauth') || verification.includes('eid');
    if (typeof verification === 'string')
      return verification === 'idauth' || verification === 'eid';
    return false;
  },
);

export const selectUserFeatures = createSelector(
  [selectUserState],
  (userState) => userState.features ?? [],
);

export const selectUserMigratedAt = createSelector(
  [selectUserState],
  (userState) => userState.migratedAt,
);

export const selectContactVerificationStatus = createSelector(
  [selectUserState],
  (userState) => userState.contactVerificationStatus ?? null,
);

export const selectAddressSource = createSelector(
  [selectUserState],
  (userState) => userState.addressSource ?? null,
);

export const selectUserSuppliedAddressFromUser = createSelector(
  [selectUserState],
  (userState) => userState.userSuppliedAddress ?? null,
);
